import Header from './Header/Header';
import Body from './Body';
import useColumns from './hooks/useColumns';
import { useLayoutState, useTimeoutLock } from './hooks/useFrame';
import { getPathValue, mergeObject, validateValue, getColumnsKey } from './utils/valueUtil';
import useStickyOffsets from './hooks/useStickyOffsets';
import ColGroup from './ColGroup';
import Panel from './Panel';
import Footer from './Footer';
import { findAllChildrenKeys, renderExpandIcon } from './utils/expandUtil';
import { getCellFixedInfo } from './utils/fixUtil';
import StickyScrollBar from './stickyScrollBar';
import useSticky from './hooks/useSticky';
import FixedHolder from './FixedHolder';
import { onUpdated, computed, defineComponent, nextTick, onMounted, onBeforeUnmount, reactive, ref, shallowRef, toRef, toRefs, watch, watchEffect, provide, customRef, } from 'vue';
import { warning } from '../vc-util/warning';
import { reactivePick } from '../_util/reactivePick';
import useState from '../_util/hooks/useState';
import { toPx } from '../_util/util';
import isVisible from '../vc-util/Dom/isVisible';
import { getTargetScrollBarSize } from '../_util/getScrollBarSize';
import getRequestAnimationFrame, { cancelRequestAnimationFrame, } from '../_util/getRequestAnimationFrame';
import classNames from '../_util/classNames';
import VCResizeObserver from '../vc-resize-observer';
import { useProvideTable } from './context/TableContext';
import { useProvideBody } from './context/BodyContext';
import { useProvideResize, useProvideInitScroll, FullTableRefKey, HasDataKey, ResizeColumnStartRefKey, ResizeColumnMoveRefKey, ResizeColumnStatusKey, } from './context/ResizeContext';
import { useProvideSticky } from './context/StickyContext';
import pickAttrs from '../_util/pickAttrs';
import { useProvideExpandedRow } from './context/ExpandedRowContext';
import ScrollBar from '../scrollbar';
import { INTERNAL_COL_DEFINE } from './utils/legacyUtil';
// Used for conditions cache
const EMPTY_DATA = [];
// Used for customize scroll
const EMPTY_SCROLL_TARGET = {};
export const INTERNAL_HOOKS = 'rc-table-internal-hook';
export default defineComponent({
    name: 'Table',
    inheritAttrs: false,
    props: [
        'prefixCls',
        'data',
        'columns',
        'rowKey',
        'tableLayout',
        'scroll',
        'rowClassName',
        'title',
        'footer',
        'id',
        'showHeader',
        'components',
        'customRow',
        'customHeaderRow',
        'direction',
        'expandFixed',
        'expandColumnWidth',
        'expandedRowKeys',
        'defaultExpandedRowKeys',
        'expandedRowRender',
        'expandRowByClick',
        'expandIcon',
        'onExpand',
        'onExpandedRowsChange',
        'onUpdate:expandedRowKeys',
        'defaultExpandAllRows',
        'indentSize',
        'expandIconColumnIndex',
        'expandedRowClassName',
        'childrenColumnName',
        'rowExpandable',
        'sticky',
        'transformColumns',
        'internalHooks',
        'internalRefs',
        'canExpandable',
        'onUpdateInternalRefs',
        'transformCellText',
        'isMaxHeight',
        'isScrollPosition',
        'virtualScroll',
    ],
    slots: ['title', 'footer', 'summary', 'emptyText'],
    emits: ['expand', 'expandedRowsChange', 'updateInternalRefs', 'update:expandedRowKeys'],
    setup(props, { attrs, slots, emit, expose }) {
        const mergedData = computed(() => props.data || EMPTY_DATA);
        const hasData = computed(() => !!mergedData.value.length);
        // ==================== Customize =====================
        const mergedComponents = computed(() => mergeObject(props.components, {}));
        const getComponent = (path, defaultComponent) => getPathValue(mergedComponents.value, path) ||
            defaultComponent;
        const getRowKey = computed(() => {
            const rowKey = props.rowKey;
            if (typeof rowKey === 'function') {
                return rowKey;
            }
            return record => {
                const key = record && record[rowKey];
                if (process.env.NODE_ENV !== 'production') {
                    warning(key !== undefined, 'Each record in table should have a unique `key` prop, or set `rowKey` to an unique primary key.');
                }
                return key;
            };
        });
        // ====================== Expand ======================
        const mergedExpandIcon = computed(() => props.expandIcon || renderExpandIcon);
        const mergedChildrenColumnName = computed(() => props.childrenColumnName || 'children');
        const expandableType = computed(() => {
            if (props.expandedRowRender) {
                return 'row';
            }
            /* eslint-disable no-underscore-dangle */
            /**
             * Fix https://github.com/ant-design/ant-design/issues/21154
             * This is a workaround to not to break current behavior.
             * We can remove follow code after final release.
             *
             * To other developer:
             *  Do not use `__PARENT_RENDER_ICON__` in prod since we will remove this when refactor
             */
            if (props.canExpandable ||
                mergedData.value.some(record => record && typeof record === 'object' && record[mergedChildrenColumnName.value])) {
                return 'nest';
            }
            /* eslint-enable */
            return false;
        });
        const innerExpandedKeys = shallowRef([]);
        const stop = watchEffect(() => {
            if (props.defaultExpandedRowKeys) {
                innerExpandedKeys.value = props.defaultExpandedRowKeys;
            }
            if (props.defaultExpandAllRows) {
                innerExpandedKeys.value = findAllChildrenKeys(mergedData.value, getRowKey.value, mergedChildrenColumnName.value);
            }
        });
        // defalutXxxx 仅仅第一次生效
        stop();
        const mergedExpandedKeys = computed(() => new Set(props.expandedRowKeys || innerExpandedKeys.value || []));
        const onTriggerExpand = record => {
            const key = getRowKey.value(record, mergedData.value.indexOf(record));
            let newExpandedKeys;
            const hasKey = mergedExpandedKeys.value.has(key);
            if (hasKey) {
                mergedExpandedKeys.value.delete(key);
                newExpandedKeys = [...mergedExpandedKeys.value];
            }
            else {
                newExpandedKeys = [...mergedExpandedKeys.value, key];
            }
            innerExpandedKeys.value = newExpandedKeys;
            emit('expand', !hasKey, record);
            emit('update:expandedRowKeys', newExpandedKeys);
            emit('expandedRowsChange', newExpandedKeys);
        };
        // Warning if use `expandedRowRender` and nest children in the same time
        if (process.env.NODE_ENV !== 'production' &&
            props.expandedRowRender &&
            mergedData.value.some(record => {
                return Array.isArray(record?.[mergedChildrenColumnName.value]);
            })) {
            warning(false, '`expandedRowRender` should not use with nested Table');
        }
        const componentWidth = ref(0);
        const [columns, flattenColumns] = useColumns({
            ...toRefs(props),
            // children,
            expandable: computed(() => !!props.expandedRowRender),
            expandedKeys: mergedExpandedKeys,
            getRowKey,
            onTriggerExpand,
            expandIcon: mergedExpandIcon,
        }, computed(() => (props.internalHooks === INTERNAL_HOOKS ? props.transformColumns : null)));
        const columnContext = computed(() => ({
            columns: columns.value,
            flattenColumns: flattenColumns.value,
        }));
        // ====================== Scroll ======================
        const fullTableRef = ref();
        const scrollHeaderRef = ref();
        const scrollBodyRef = ref();
        const scrollBarRef = ref();
        const resizeColumnMoveRef = ref();
        const resizeColumnStartRef = ref();
        const isResizingHandle = ref(false);
        const scrollBodySizeInfo = ref({
            scrollWidth: 0,
            clientWidth: 0,
        });
        const scrollSummaryRef = ref();
        const [pingedLeft, setPingedLeft] = useState(false);
        const [pingedRight, setPingedRight] = useState(false);
        const [colsWidths, updateColsWidths] = useLayoutState(new Map());
        const layout = reactive({
            scrollX: false,
            scrollY: false,
        });
        // 滚动的方向
        const scrollDirection = reactive({
            left: false,
            middle: false,
            right: false,
        });
        // Convert map to number width
        const colsKeys = computed(() => getColumnsKey(flattenColumns.value));
        const colWidths = computed(() => colsKeys.value.map(columnKey => colsWidths.value.get(columnKey)));
        const columnCount = computed(() => flattenColumns.value.length);
        const stickyOffsets = useStickyOffsets(colWidths, columnCount, toRef(props, 'direction'));
        const fixHeader = computed(() => props.scroll && validateValue(props.scroll.y));
        const horizonScroll = computed(() => (props.scroll && validateValue(props.scroll.x)) || Boolean(props.expandFixed));
        const fixColumn = computed(() => horizonScroll.value && flattenColumns.value.some(({ fixed }) => fixed));
        // Sticky
        const stickyRef = ref();
        const stickyState = useSticky(toRef(props, 'sticky'), toRef(props, 'prefixCls'));
        const summaryFixedInfos = reactive({});
        const fixFooter = computed(() => {
            const info = Object.values(summaryFixedInfos)[0];
            return (fixHeader.value || stickyState.value.isSticky) && info;
        });
        const summaryCollect = (uniKey, fixed) => {
            if (fixed) {
                summaryFixedInfos[uniKey] = fixed;
            }
            else {
                delete summaryFixedInfos[uniKey];
            }
        };
        // Scroll
        const scrollXStyle = ref({});
        const scrollYStyle = ref({});
        const scrollTableStyle = ref({});
        watchEffect(() => {
            if (fixHeader.value) {
                scrollYStyle.value = {
                    // overflowY: props.data?.length > 0 ? 'scroll' : 'hidden',
                    // overflowY: props.data?.length > 0 ? 'auto' : 'hidden',
                    maxHeight: toPx(props.scroll.y),
                };
            }
            if (horizonScroll.value) {
                // scrollXStyle.value = { overflowX: props.data?.length > 0 ? 'auto' : 'hidden' };
                // When no vertical scrollbar, should hide it
                // https://github.com/ant-design/ant-design/pull/20705
                // https://github.com/ant-design/ant-design/issues/21879
                if (!fixHeader.value) {
                    // scrollYStyle.value = { overflowY: 'hidden' };
                }
                scrollTableStyle.value = {
                    // width: props.scroll.x === true ? 'auto' : toPx(props.scroll.x),
                    minWidth: '100%',
                };
            }
        });
        const onColumnResize = (columnKey, width) => {
            if (isVisible(fullTableRef.value)) {
                updateColsWidths(widths => {
                    if (widths.get(columnKey) !== width) {
                        const newWidths = new Map(widths);
                        newWidths.set(columnKey, width);
                        return newWidths;
                    }
                    return widths;
                });
            }
        };
        const [setScrollTarget, getScrollTarget] = useTimeoutLock(null);
        function forceScroll(scrollLeft, target) {
            if (!target) {
                return;
            }
            if (typeof target === 'function') {
                target(scrollLeft);
                return;
            }
            const domTarget = target.$el || target;
            if (domTarget.scrollLeft !== scrollLeft) {
                // eslint-disable-next-line no-param-reassign
                domTarget.scrollLeft = scrollLeft;
            }
        }
        const onScroll = ({ currentTarget, scrollLeft, }) => {
            const isRTL = props.direction === 'rtl';
            const mergedScrollLeft = typeof scrollLeft === 'number' ? scrollLeft : currentTarget.scrollLeft;
            const compareTarget = currentTarget || EMPTY_SCROLL_TARGET;
            if (!getScrollTarget() || getScrollTarget() === compareTarget) {
                setScrollTarget(compareTarget);
                // forceScroll(mergedScrollLeft, scrollHeaderRef.value);
                forceScroll(mergedScrollLeft, scrollBodyRef.value);
                forceScroll(mergedScrollLeft, scrollSummaryRef.value);
                forceScroll(mergedScrollLeft, stickyRef.value?.setScrollLeft);
            }
            if (currentTarget) {
                const { scrollWidth, clientWidth } = currentTarget;
                if (isRTL) {
                    setPingedLeft(-mergedScrollLeft < scrollWidth - clientWidth);
                    setPingedRight(-mergedScrollLeft > 0);
                }
                else {
                    setPingedLeft(mergedScrollLeft > 0);
                    setPingedRight(mergedScrollLeft < scrollWidth - clientWidth);
                }
            }
        };
        const triggerOnScroll = () => {
            if (horizonScroll.value && scrollBodyRef.value) {
                onScroll({ currentTarget: scrollBodyRef.value });
            }
            else {
                setPingedLeft(false);
                setPingedRight(false);
            }
        };
        let timtout;
        const updateWidth = (width) => {
            if (width !== componentWidth.value) {
                triggerOnScroll();
                componentWidth.value = fullTableRef.value ? fullTableRef.value.offsetWidth : width;
            }
        };
        const onFullTableResize = ({ width }) => {
            clearTimeout(timtout);
            if (componentWidth.value === 0) {
                updateWidth(width);
                return;
            }
            timtout = setTimeout(() => {
                updateWidth(width);
            }, 100);
            // 表格宽度发生变化时初始化表格滚动状态
            onInitTableScroll();
        };
        const isScrollBar = ref(false);
        watch([horizonScroll, () => props.data, () => props.columns], () => {
            if (horizonScroll.value) {
                triggerOnScroll();
            }
            nextTick(() => {
                isScrollBar.value =
                    scrollBodyRef.value?.querySelector('table')?.offsetHeight >
                        scrollBodyRef.value?.offsetHeight;
                // columns发生变化时初始化表格滚动状态
                onInitTableScroll();
            });
        }, { flush: 'post' });
        const onInitTableScroll = () => {
            let bodyMinWidth = 0;
            flattenColumns.value.forEach(column => {
                bodyMinWidth += Number(column.width || column.minWidth || 0);
            });
            let bodyWidth = fullTableRef.value?.clientWidth ?? 0;
            const selectColumn = flattenColumns.value.find(column => INTERNAL_COL_DEFINE in column);
            if (selectColumn?.className && fullTableRef.value) {
                const selectEl = fullTableRef.value.querySelector(`.${selectColumn.className}`);
                const selectRect = selectEl.getBoundingClientRect();
                bodyWidth = bodyWidth - selectRect.width;
            }
            layout.scrollX = bodyMinWidth > bodyWidth;
            syncPosition();
        };
        const [scrollbarSize, setScrollbarSize] = useState(0);
        useProvideSticky();
        onMounted(() => {
            nextTick(() => {
                triggerOnScroll();
                setScrollbarSize(getTargetScrollBarSize(scrollBodyRef.value).width);
                scrollBodySizeInfo.value = {
                    scrollWidth: scrollBodyRef.value?.scrollWidth || 0,
                    clientWidth: scrollBodyRef.value?.clientWidth || 0,
                };
                onInitTableScroll();
            });
        });
        onUpdated(() => {
            nextTick(() => {
                const scrollWidth = scrollBodyRef.value?.scrollWidth || 0;
                const clientWidth = scrollBodyRef.value?.clientWidth || 0;
                if (scrollBodySizeInfo.value.scrollWidth !== scrollWidth ||
                    scrollBodySizeInfo.value.clientWidth !== clientWidth) {
                    scrollBodySizeInfo.value = {
                        scrollWidth,
                        clientWidth,
                    };
                }
                if (props.virtualScroll?.isOpen) {
                    virtualScrollInit();
                }
            });
        });
        // 记录得到每一行容器尺寸大小
        const sizeMap = new Map();
        // 记录得到每一行被合并的首项下标和结束下标
        const rowspanStartEndIndexMap = new Map();
        const requestAnimationFrame = getRequestAnimationFrame();
        let virtualScrollRafId = null;
        const cancelVirtualScrollSchedule = () => {
            if (virtualScrollRafId !== null) {
                cancelRequestAnimationFrame(virtualScrollRafId);
                virtualScrollRafId = null;
            }
        };
        // 组件卸载时清理资源
        onBeforeUnmount(() => {
            if (timtout)
                clearTimeout(timtout);
            cancelVirtualScrollSchedule();
            // 清理Map缓存，避免内存堆积
            sizeMap.clear();
            rowspanStartEndIndexMap.clear();
        });
        watchEffect(() => {
            if (props.internalHooks === INTERNAL_HOOKS && props.internalRefs) {
                props.onUpdateInternalRefs({
                    body: scrollBodyRef.value
                        ? scrollBodyRef.value.$el || scrollBodyRef.value
                        : null,
                });
            }
        }, { flush: 'post' });
        // Table layout
        const mergedTableLayout = computed(() => {
            if (props.tableLayout) {
                return props.tableLayout;
            }
            // https://github.com/ant-design/ant-design/issues/25227
            // When scroll.x is max-content, no need to fix table layout
            // it's width should stretch out to fit content
            if (fixColumn.value) {
                return props.scroll.x === 'max-content' ? 'auto' : 'fixed';
            }
            if (fixHeader.value ||
                stickyState.value.isSticky ||
                flattenColumns.value.some(({ ellipsis }) => ellipsis)) {
                return 'fixed';
            }
            return 'auto';
        });
        const emptyNode = () => {
            return hasData.value ? null : slots.emptyText?.() || 'No Data';
        };
        useProvideTable(reactive({
            ...toRefs(reactivePick(props, 'prefixCls', 'direction', 'transformCellText')),
            getComponent,
            scrollbarSize,
            fixedInfoList: computed(() => flattenColumns.value.map((_, colIndex) => getCellFixedInfo(colIndex, colIndex, flattenColumns.value, stickyOffsets.value, props.direction))),
            isSticky: computed(() => stickyState.value.isSticky),
            summaryCollect,
        }));
        useProvideBody(reactive({
            ...toRefs(reactivePick(props, 'rowClassName', 'expandedRowClassName', 'expandRowByClick', 'expandedRowRender', 'expandIconColumnIndex', 'indentSize')),
            columns,
            flattenColumns,
            tableLayout: mergedTableLayout,
            expandIcon: mergedExpandIcon,
            expandableType,
            onTriggerExpand,
        }));
        useProvideResize({
            onColumnResize,
        });
        useProvideInitScroll({
            onInitTableScroll,
        });
        provide(ResizeColumnMoveRefKey, resizeColumnMoveRef);
        provide(FullTableRefKey, fullTableRef);
        provide(HasDataKey, hasData);
        provide(ResizeColumnStartRefKey, resizeColumnStartRef);
        provide(ResizeColumnStatusKey, isResizingHandle);
        useProvideExpandedRow({
            componentWidth,
            fixHeader,
            fixColumn,
            horizonScroll,
        });
        // 滚动时同步表头位置
        const handleScrollBar = () => {
            syncPosition();
        };
        const hasScrollClass = (className) => {
            return !!(fullTableRef.value && fullTableRef.value.classList.contains(className));
        };
        // 高度变化
        const dymysicHeight = shallowRef(0);
        const list = customRef((track, trigger) => {
            return {
                get() {
                    track();
                    return !props.virtualScroll?.isOpen
                        ? mergedData.value
                        : mergedData.value.slice(saveIndex.value.startIndex, saveIndex.value.endIndex);
                },
                set() {
                    trigger();
                },
            };
        });
        const rowHeight = ref(32); // 默认32
        // 计算出每一个item到滚动容器顶部的距离
        const offsetMap = () => {
            const res = {};
            let total = 0;
            for (let i = 0; i < mergedData.value.length; i++) {
                const val = mergedData.value?.[i];
                const key = getRowKey.value(val);
                res[key] = total;
                const curSize = sizeMap.get(key);
                const size = typeof curSize === 'number' ? curSize : rowHeight.value;
                total += size;
            }
            return res;
        };
        const isTreeStructure = (listData) => {
            return listData.some(k => k?.[mergedChildrenColumnName.value] && Array.isArray(k?.[mergedChildrenColumnName.value]));
        };
        // 更新每行的纵向合并首项
        const updateRowSpan = () => {
            let rows = [];
            if (isTreeStructure(list.value)) {
                // add： 需要判断是否为树状结构
                rows = scrollBodyRef.value?.querySelectorAll(`.${props.prefixCls}-body .${props.prefixCls}-tbody .${props.prefixCls}-row-level-0`);
            }
            else {
                rows = scrollBodyRef.value?.querySelectorAll(`.${props.prefixCls}-body .${props.prefixCls}-tbody .${props.prefixCls}-row`);
            }
            if (!rows)
                return;
            Array.prototype.forEach.call(rows, (row, idx) => {
                const item = list.value[idx];
                if (!item)
                    return;
                // 获取当前行的所有有rowspan的cell
                let rowspanCellList = row?.querySelectorAll(`.${props.prefixCls}-cell-is-row-span`);
                // 获取cell中的所有rowspan，并给被合并项标记合并首项的最小值
                Array.prototype.forEach.call(rowspanCellList, (cell) => {
                    const rowspan = +cell.getAttribute('rowspan');
                    const startIndex = saveIndex.value.startIndex + idx;
                    const endIndex = startIndex + rowspan - 1;
                    for (let i = 0; i < rowspan; i++) {
                        const val = mergedData.value[startIndex + i];
                        if (!val)
                            return;
                        const key = getRowKey.value(val);
                        const rowspanStartIndex = rowspanStartEndIndexMap.get(key);
                        if (rowspanStartIndex) {
                            const [start, end] = rowspanStartIndex;
                            const startVal = Math.min(start, startIndex);
                            const EndVal = Math.max(end, endIndex);
                            rowspanStartEndIndexMap.set(key, [startVal, EndVal]);
                        }
                        else {
                            rowspanStartEndIndexMap.set(key, [startIndex, endIndex]);
                        }
                    }
                });
            });
        };
        const getRowSpan = (start, end, searchIndex) => {
            const item = mergedData.value?.[searchIndex];
            if (!item)
                return [start, end];
            const key = getRowKey.value(item);
            const val = rowspanStartEndIndexMap.get(key) ?? [start, end];
            return val;
        };
        // 更新每一行动态尺寸
        const updateSize = () => {
            let rows = [];
            if (isTreeStructure(list.value)) {
                // add： 需要判断是否为树状结构
                rows = scrollBodyRef.value?.querySelectorAll(`.${props.prefixCls}-body .${props.prefixCls}-tbody .${props.prefixCls}-row-level-0`);
            }
            else {
                // todo： 如果是树形结构需要计算嵌套基层并加上里面嵌套层高度
                rows = scrollBodyRef.value?.querySelectorAll(`.${props.prefixCls}-body .${props.prefixCls}-tbody .${props.prefixCls}-row`);
            }
            if (!rows)
                return;
            const rowHeights = [];
            Array.prototype.forEach.call(rows, (row, idx) => {
                const item = list.value[idx];
                if (!item)
                    return;
                // 计算表格行的高度
                let offsetHeightRow = row.offsetHeight;
                rowHeights.push(offsetHeightRow);
                if (!props.virtualScroll?.isDynamic)
                    return;
                // 如果表格行有扩展项，需要加上扩张向的高度
                const nextRow = row?.nextElementSibling;
                if (nextRow &&
                    nextRow.classList &&
                    nextRow.classList.contains(`${props.prefixCls}-expanded-row`)) {
                    offsetHeightRow += nextRow.nextElementSibling.offsetHeight;
                }
                // 表格行如果有子孙节点，需要加上子孙节点 高度
                let next = row?.nextElementSibling;
                while (!!next &&
                    next.tagName === 'TR' &&
                    !next.classList.contains(`${props.prefixCls}-row-level-0`)) {
                    offsetHeightRow += next.offsetHeight;
                    next = next.nextElementSibling;
                }
                const key = getRowKey.value(item);
                if (sizeMap.get(key) !== offsetHeightRow) {
                    sizeMap.set(item.key, offsetHeightRow);
                    row._offsetHeight = offsetHeightRow;
                }
            });
        };
        // 设置滚动状态方向
        const setScrollDirection = (direction) => {
            Object.keys(scrollDirection).forEach(key => {
                if (key === direction) {
                    scrollDirection[direction] = true;
                }
                else {
                    scrollDirection[key] = false;
                }
            });
        };
        const saveIndex = ref({
            startIndex: 0,
            endIndex: 0,
        });
        const positiontransformY = shallowRef(0);
        // 获取某条数据的尺寸
        const getItemSize = (index) => {
            // 先从缓存中获取
            if (index < 0)
                return 0;
            const item = list.value?.[index];
            if (item) {
                const key = getRowKey.value(item);
                return sizeMap.get(key) ?? rowHeight.value;
            }
            return rowHeight.value;
        };
        // 获取某条数据的offsetTop
        const getItemOffsetTop = (index) => {
            // 固定行高就直接相乘计算
            if (!props.virtualScroll?.isDynamic) {
                return index * rowHeight.value;
            }
            const item = mergedData.value?.[index];
            if (item) {
                const obj = offsetMap();
                const key = getRowKey.value(item);
                return obj[key] ?? 32;
            }
            return 0;
        };
        // 计算动态滚动高度
        const calcPositionFun = () => {
            const last = mergedData.value.length - 1;
            // 计算内容总高度
            const totalHeight = getItemOffsetTop(last) + getItemSize(last);
            // 计算当前滚动位置需要撑起的高度
            const offsetTop = getItemOffsetTop(saveIndex.value.startIndex);
            dymysicHeight.value = totalHeight;
            positiontransformY.value = offsetTop;
        };
        // 计算视图上渲染的数据位置
        const getCalcRenderData = () => {
            const scrollContainer = scrollBarRef.value.wrapRef;
            const { scrollTop, offsetHeight } = scrollContainer;
            // 先默认固定缓存5条32*5=160
            const BUFFER = props.virtualScroll?.buffer || 0;
            const top = Math.max(scrollTop - BUFFER, 0);
            // 可视区底部
            const bottom = scrollTop + offsetHeight + BUFFER;
            let start;
            let end;
            // 这里区分动态计算行高度，不定高的情况 目前固定为动态计算
            let l = 0;
            let r = mergedData.value.length - 1;
            let mid = 0;
            // 二分法查找滚动最优项
            while (l <= r) {
                // 当先位置到底部之间的中间值，向下取整
                mid = Math.floor((l + r) / 2);
                // 计算中间数据距离顶部距离
                const midVal = getItemOffsetTop(mid);
                if (midVal < top) {
                    const midNextVal = getItemOffsetTop(mid + 1);
                    if (midNextVal > top)
                        break;
                    l = mid + 1;
                }
                else {
                    r = mid - 1;
                }
            }
            // 计算渲染内容的开始、结束索引
            start = mid;
            end = mergedData.value.length;
            for (let i = start + 1; i < mergedData.value.length; i++) {
                const offsetTop = getItemOffsetTop(i);
                if (offsetTop >= bottom) {
                    end = i;
                    break;
                }
            }
            // 如果当前首项是被合并项，则start要取合并首项,end取合并尾项
            start = Math.min(mid, getRowSpan(mid, end, mid)[0]);
            end = Math.max(end, getRowSpan(start, end, end)[1] + 1);
            // 开始索引始终保持偶数，如果为奇数，则加1使其保持偶数【确保表格行的偶数数一致，不会导致斑马纹乱序显示】
            if (start % 2) {
                start = start - 1;
            }
            saveIndex.value.startIndex = start;
            saveIndex.value.endIndex = end;
        };
        const runVirtualScrollTasks = () => {
            if (!props.virtualScroll?.isOpen) {
                return;
            }
            updateRowSpan();
            updateSize();
            getCalcRenderData();
            calcPositionFun();
        };
        const virtualScrollInit = () => {
            if (!props.virtualScroll?.isOpen) {
                return;
            }
            cancelVirtualScrollSchedule();
            virtualScrollRafId = requestAnimationFrame(() => {
                virtualScrollRafId = null;
                runVirtualScrollTasks();
            });
        };
        const syncPosition = () => {
            if (!scrollBarRef.value)
                return;
            const scrollContainer = scrollBarRef.value.wrapRef;
            const { scrollLeft, offsetWidth, scrollWidth } = scrollContainer;
            if (props.virtualScroll?.isOpen) {
                virtualScrollInit();
            }
            if (!layout.scrollX) {
                const scrollingNoneClass = 'is-scrolling-none';
                if (!hasScrollClass(scrollingNoneClass)) {
                    setScrollDirection();
                }
                return;
            }
            if (!scrollContainer)
                return;
            if (scrollHeaderRef.value)
                scrollHeaderRef.value.$el.scrollLeft = scrollLeft;
            const maxScrollLeftPosition = scrollWidth - offsetWidth - 1;
            if (scrollLeft >= maxScrollLeftPosition) {
                setScrollDirection('right');
            }
            else if (scrollLeft === 0) {
                setScrollDirection('left');
            }
            else {
                setScrollDirection('middle');
            }
        };
        // 获取滚动条实例方法
        const getScrollbar = () => {
            return scrollBarRef.value;
        };
        expose({
            getScrollbar,
        });
        // Body
        const bodyTable = () => (<Body data={list.value} measureColumnWidth={fixHeader.value || horizonScroll.value || stickyState.value.isSticky} expandedKeys={mergedExpandedKeys.value} rowExpandable={props.rowExpandable} getRowKey={getRowKey.value} customRow={props.customRow} childrenColumnName={mergedChildrenColumnName.value} v-slots={{ emptyNode }} style={props.virtualScroll?.isOpen
                ? {
                    transform: `translateY(${positiontransformY.value}px)`,
                }
                : null}/>);
        const bodyColGroup = () => (<ColGroup colWidths={flattenColumns.value.map(({ width }) => width)} columns={flattenColumns.value}/>);
        // 滚动条内容区域样式
        const scrollbarStyle = computed(() => {
            if (!mergedData.value.length) {
                return {
                    overflow: 'hidden',
                };
            }
            if (props.scroll.y) {
                return props.isMaxHeight
                    ? { height: '100%', ...scrollYStyle.value }
                    : {
                        height: '100%',
                    };
            }
            return {};
        });
        // 滚动条视图区域样式
        const scrollbarViewStyle = props.isScrollPosition
            ? {
                display: 'inline-block',
                verticalAlign: 'middle',
                position: 'relative',
            }
            : {
                display: 'inline-block',
                verticalAlign: 'middle',
            };
        return () => {
            const { prefixCls, scroll, tableLayout, direction, 
            // Additional Part
            title = slots.title, footer = slots.footer, 
            // Customize
            id, showHeader, customHeaderRow, } = props;
            const { isSticky, offsetHeader, offsetSummary, offsetScroll, stickyClassName, container } = stickyState.value;
            const TableComponent = getComponent(['table'], 'table');
            const customizeScrollBody = getComponent(['body']);
            const summaryNode = slots.summary?.({ pageData: mergedData.value });
            let groupTableNode = () => null;
            // Header props
            const headerProps = {
                colWidths: colWidths.value,
                columCount: flattenColumns.value.length,
                stickyOffsets: stickyOffsets.value,
                customHeaderRow,
                fixHeader: fixHeader.value,
                scroll,
            };
            if (process.env.NODE_ENV !== 'production' &&
                typeof customizeScrollBody === 'function' &&
                hasData.value &&
                !fixHeader.value) {
                warning(false, '`components.body` with render props is only work on `scroll.y`.');
            }
            if (fixHeader.value || isSticky) {
                // >>>>>> Fixed Header
                let bodyContent = () => null;
                if (typeof customizeScrollBody === 'function') {
                    bodyContent = () => customizeScrollBody(mergedData.value, {
                        scrollbarSize: scrollbarSize.value,
                        ref: scrollBodyRef,
                        onScroll,
                    });
                    headerProps.colWidths = flattenColumns.value.map(({ width }, index) => {
                        const colWidth = index === columns.value.length - 1 ? width - scrollbarSize.value : width;
                        if (typeof colWidth === 'number' && !Number.isNaN(colWidth)) {
                            return colWidth;
                        }
                        warning(false, 'When use `components.body` with render props. Each column should have a fixed `width` value.');
                        return 0;
                    });
                }
                else {
                    let scrollbarInlineStyle = null;
                    if (props.isMaxHeight) {
                        scrollbarInlineStyle = {
                            ...scrollYStyle.value,
                        };
                    }
                    bodyContent = () => (<div style={{
                            ...scrollYStyle.value,
                        }} ref={scrollBodyRef} class={classNames(`${prefixCls}-body`)}>
              <ScrollBar style={scrollbarInlineStyle} ref={scrollBarRef} wrap-style={scrollbarStyle.value} view-style={scrollbarViewStyle} onScroll={handleScrollBar}>
                <TableComponent style={{
                            ...scrollTableStyle.value,
                            tableLayout: mergedTableLayout.value,
                        }}>
                  {props.virtualScroll?.isOpen ? (<div style={{
                                height: dymysicHeight.value + 'px',
                                position: 'absolute',
                                width: '100%',
                                zIndex: -1,
                            }}></div>) : null}
                  {bodyColGroup()}
                  {bodyTable()}
                  {!fixFooter.value && summaryNode && (<Footer stickyOffsets={stickyOffsets.value} flattenColumns={flattenColumns.value}>
                      {summaryNode}
                    </Footer>)}
                </TableComponent>
              </ScrollBar>
            </div>);
                }
                // Fixed holder share the props
                const fixedHolderProps = {
                    noData: !mergedData.value.length,
                    maxContentScroll: horizonScroll.value && scroll.x === 'max-content',
                    ...headerProps,
                    ...columnContext.value,
                    direction,
                    stickyClassName,
                    onScroll,
                };
                groupTableNode = () => (<>
            {/* Header Table */}
            {showHeader !== false && (<FixedHolder {...fixedHolderProps} stickyTopOffset={offsetHeader} class={`${prefixCls}-header`} isScrollBar={false} 
                    // isScrollBar={isScrollBar.value}
                    ref={scrollHeaderRef} v-slots={{
                            default: fixedHolderPassProps => (<>
                      <Header {...fixedHolderPassProps}/>
                      {fixFooter.value === 'top' && (<Footer {...fixedHolderPassProps}>{summaryNode}</Footer>)}
                    </>),
                        }}></FixedHolder>)}
            {/* Body Table */}
            {bodyContent()}
            {/* Summary Table */}
            {fixFooter.value && fixFooter.value !== 'top' && (<FixedHolder {...fixedHolderProps} stickyBottomOffset={offsetSummary} class={`${prefixCls}-summary`} isScrollBar={false} 
                    // isScrollBar={isScrollBar.value}
                    ref={scrollSummaryRef} v-slots={{
                            default: fixedHolderPassProps => (<Footer {...fixedHolderPassProps}>{summaryNode}</Footer>),
                        }}></FixedHolder>)}

            {isSticky && scrollBodyRef.value && (<StickyScrollBar ref={stickyRef} offsetScroll={offsetScroll} scrollBodyRef={scrollBodyRef} onScroll={onScroll} container={container} scrollBodySizeInfo={scrollBodySizeInfo.value}/>)}
          </>);
            }
            else {
                // >>>>>> Unique table
                groupTableNode = () => (<div style={{
                        ...scrollXStyle.value,
                        ...scrollYStyle.value,
                    }} class={classNames(`${prefixCls}-content`)} onScroll={onScroll} ref={scrollBodyRef}>
            <TableComponent style={{ ...scrollTableStyle.value, tableLayout: mergedTableLayout.value }}>
              {bodyColGroup()}
              {showHeader !== false && <Header {...headerProps} {...columnContext.value}/>}
              {bodyTable()}
              {summaryNode && (<Footer stickyOffsets={stickyOffsets.value} flattenColumns={flattenColumns.value}>
                  {summaryNode}
                </Footer>)}
            </TableComponent>
          </div>);
            }
            const ariaProps = pickAttrs(attrs, { aria: true, data: true });
            const fullTable = () => {
                return (<div {...ariaProps} class={classNames(prefixCls, {
                        [`${prefixCls}-rtl`]: direction === 'rtl',
                        [`${prefixCls}-ping-left`]: pingedLeft.value,
                        [`${prefixCls}-ping-right`]: pingedRight.value,
                        [`${prefixCls}-layout-fixed`]: tableLayout === 'fixed',
                        [`${prefixCls}-fixed-header`]: fixHeader.value,
                        /** No used but for compatible */
                        [`${prefixCls}-fixed-column`]: fixColumn.value,
                        [`${prefixCls}-scroll-horizontal`]: horizonScroll.value,
                        [`${prefixCls}-has-fix-left`]: flattenColumns.value[0] && flattenColumns.value[0].fixed,
                        [`${prefixCls}-has-fix-right`]: flattenColumns.value[columnCount.value - 1] &&
                            flattenColumns.value[columnCount.value - 1].fixed === 'right',
                        [attrs.class]: attrs.class,
                        ['is-scrolling-left']: scrollDirection.left,
                        ['is-scrolling-right']: scrollDirection.right,
                        ['is-scrolling-middle']: scrollDirection.middle,
                        ['is-scrolling-none']: !scrollDirection.left && !scrollDirection.middle && !scrollDirection.right,
                        ['is-resizing-handle']: isResizingHandle.value,
                        // [`${prefixCls}-scrollbar`]: isScrollBar.value,
                    })} style={attrs.style} id={id} ref={fullTableRef}>
            {title && <Panel class={`${prefixCls}-title`}>{title(mergedData.value)}</Panel>}
            <div class={`${prefixCls}-container`}>{groupTableNode()}</div>
            <div class={`${prefixCls}-resize-start`} ref={resizeColumnStartRef}></div>
            <div class={`${prefixCls}-resize-move`} ref={resizeColumnMoveRef}></div>
            {footer && <Panel class={`${prefixCls}-footer`}>{footer(mergedData.value)}</Panel>}
          </div>);
            };
            if (horizonScroll.value) {
                return (<VCResizeObserver onResize={onFullTableResize} v-slots={{ default: fullTable }}></VCResizeObserver>);
            }
            return fullTable();
        };
    },
});
